import { formatPrice } from '@/common/constant';
import type { DishAnalysis } from '@/common/generated/wine-dish-analysis.typebox';
import type { WineRecommendation } from '@/common/generated/wine-dish-recommendation.typebox';
import { RequireAuth } from '@/components/PermissionLink';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { EMPTY_IMAGE_FALLBACK } from '@/lib/constants';
import {
  Badge,
  Button,
  Card,
  Descriptions,
  Drawer,
  Image,
  Skeleton,
  Tag,
} from 'antd';
import type { FC } from 'react';
import { useState } from 'react';
import { getPairingStyleColor } from '../utils';
import EditWineImageModal from './EditWineImageModal';

interface WineDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  wine?: WineRecommendation;
  dishAnalysis?: DishAnalysis;
  alternativeWines?: WineRecommendation[];
  onWineSelect?: (wine: WineRecommendation) => void;
}

export const WineDetailDrawer: FC<WineDetailDrawerProps> = ({
  open,
  onClose,
  wine,
  dishAnalysis,
  alternativeWines,
  onWineSelect,
}) => {
  const { currency } = useCurrencySymbol();
  const [editImageModalVisible, setEditImageModalVisible] = useState(false);

  const handleWineSelect = (newWine: WineRecommendation) => {
    onClose();
    setTimeout(() => {
      onWineSelect?.(newWine);
    }, 300);
  };

  return (
    <>
      <Drawer
        title={wine?.name}
        placement="right"
        width={1080}
        onClose={onClose}
        open={open}
        destroyOnClose
        extra={
          <RequireAuth permissions={['admin']} fallback={null}>
            <div className="flex gap-2">
              <Button
                type="default"
                onClick={() => setEditImageModalVisible(true)}
              >
                Edit Image
              </Button>
            </div>
          </RequireAuth>
        }
      >
        {wine != null ? (
          <div className="flex flex-col gap-8">
            {/* Section 1: Dish Analysis and Wine Basic Info */}
            <div className="flex gap-6">
              <div className="flex-1">
                <h3 className="text-lg font-bold mb-4">Dish Analysis</h3>
                {dishAnalysis && (
                  <div className="space-y-6">
                    <div>
                      <div className="text-2xl font-semibold mb-2">
                        {dishAnalysis.name}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-2">
                        Intensity
                      </div>
                      <div className="text-2xl font-semibold capitalize">
                        {dishAnalysis.overallIntensity}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-2">
                        Key Flavors
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {dishAnalysis.flavorProfile.dominant.map((flavor) => (
                          <Tag key={flavor}>{flavor}</Tag>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-2">
                        Main Ingredients
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {dishAnalysis.ingredients
                          .filter((i) => i.role === 'primary')
                          .map((ingredient) => (
                            <Tag key={ingredient.name}>{ingredient.name}</Tag>
                          ))}
                      </div>
                    </div>
                    {dishAnalysis.cookingMethods.length > 0 && (
                      <div>
                        <div className="text-sm text-gray-500 mb-2">
                          Preparation
                        </div>
                        <div className="text-base">
                          {dishAnalysis.cookingMethods
                            .map((m) => m.technique)
                            .join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex flex-col gap-4">
                  <Image
                    width={240}
                    height={320}
                    alt={wine.name}
                    src={wine.productImageUrl}
                    placeholder={true}
                    className="object-contain"
                    fallback={EMPTY_IMAGE_FALLBACK}
                  />
                  <div className="flex items-center gap-2">
                    <Tag color="purple">{wine.type}</Tag>
                    <Tag
                      color={getPairingStyleColor(
                        wine.pairingRationale.pairingStyle,
                      )}
                    >
                      {wine.pairingRationale.pairingStyle}
                    </Tag>
                    <span className="text-gray-500">{wine.region}</span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Varietal</div>
                    <div className="text-lg">{wine.varietal}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Price</div>
                    <div className="text-lg font-bold">
                      {formatPrice(wine.price, currency)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Pairing Rationale */}
            <div>
              <h3 className="text-lg font-bold mb-4">Pairing Rationale</h3>
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Match Rationale</div>
                  <div>{wine.pairingRationale.matchRationale}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Texture Match</div>
                  <div>{wine.pairingRationale.textureMatch}</div>
                </div>
              </div>
            </div>

            {/* Section 3: Flavor Bridge */}
            <div>
              <h3 className="text-lg font-bold mb-4">Flavor Bridge</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {wine.pairingRationale.flavorBridge.map((flavor) => (
                  <Card
                    key={flavor}
                    size="small"
                    className="hover:shadow-md transition-shadow text-left"
                  >
                    {flavor}
                  </Card>
                ))}
              </div>
            </div>

            {/* Section 4: Serving Tips */}
            <div>
              <h3 className="text-lg font-bold mb-4">Serving Tips</h3>
              <div className="space-y-3">
                {wine.pairingRationale.servingTips.map((tip, index) => (
                  <div key={tip} className="flex items-center gap-3">
                    <Badge
                      count={index + 1}
                      style={{
                        backgroundColor: '#F3F0FF',
                        color: '#8B5CF6',
                        marginTop: 0,
                        fontWeight: 600,
                      }}
                    />
                    <span>{tip}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 5: Pairing Story */}
            <div>
              <h3 className="text-lg font-bold mb-4">Pairing Story</h3>
              <div className="space-y-4">
                <p className="text-gray-700">
                  {wine.pairingRationale.pairingStory.story}
                </p>
              </div>
            </div>

            {/* Section 6: Key Highlights */}
            <div>
              <h3 className="text-lg font-bold mb-4">Key Highlights</h3>
              <ul className="list-disc list-inside space-y-2">
                {wine.pairingRationale.pairingStory.highlights.map(
                  (highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ),
                )}
              </ul>
            </div>

            {/* Section 7: Wine Characteristics */}
            <div>
              <h3 className="text-lg font-bold mb-4">Wine Characteristics</h3>
              <Descriptions
                column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }}
                bordered
                size="small"
                className="bg-white"
                items={[
                  {
                    key: 'body',
                    label: 'Body',
                    children: wine.characteristics.body,
                  },
                  {
                    key: 'acidity',
                    label: 'Acidity',
                    children: wine.characteristics.acidity,
                  },
                  ...(wine.characteristics.tannins
                    ? [
                        {
                          key: 'tannins',
                          label: 'Tannins',
                          children: wine.characteristics.tannins,
                        },
                      ]
                    : []),
                  {
                    key: 'sweetness',
                    label: 'Sweetness',
                    children: wine.characteristics.sweetness,
                  },
                  {
                    key: 'agingPotential',
                    label: 'Aging Potential',
                    children: wine.agingPotential,
                  },
                  {
                    key: 'servingTemp',
                    label: 'Serving Temperature',
                    children: `${wine.servingTemp.min}°C - ${wine.servingTemp.max}°C`,
                  },
                ]}
              />
            </div>

            {/* Section 8: Alternative Pairings */}
            {alternativeWines && alternativeWines.length > 0 && (
              <div>
                <h3 className="text-lg font-bold mb-4">Alternative Pairings</h3>
                <div className="grid grid-cols-2 gap-4">
                  {alternativeWines.map((altWine) => (
                    <button
                      type="button"
                      key={altWine.productId}
                      className="w-full text-left bg-gray-50 p-4 rounded-md cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleWineSelect?.(altWine)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleWineSelect?.(altWine);
                        }
                      }}
                    >
                      <div className="flex items-start gap-4">
                        <Image
                          src={altWine.productImageUrl}
                          alt={altWine.name}
                          width={64}
                          height={96}
                          className="object-contain"
                          placeholder={true}
                          fallback={EMPTY_IMAGE_FALLBACK}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium mb-1 break-words">
                            {altWine.name}
                          </div>
                          <div className="text-sm text-gray-500 mb-1">
                            {altWine.varietal}
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <Tag color="purple">{altWine.type}</Tag>
                            <span className="text-xs text-gray-500">
                              {altWine.region}
                            </span>
                          </div>
                          <div className="text-sm font-semibold">
                            {formatPrice(altWine.price, currency)}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <Skeleton active />
        )}
      </Drawer>

      {wine && (
        <EditWineImageModal
          visible={editImageModalVisible}
          onCancel={() => setEditImageModalVisible(false)}
          onSuccess={(newImageUrl) => {
            setEditImageModalVisible(false);
            // Refresh the drawer content
            onClose();
            setTimeout(() => {
              onWineSelect?.({
                ...wine,
                productImageUrl: newImageUrl,
              });
            }, 300);
          }}
          productId={wine.productId}
          currentImageUrl={wine.productImageUrl}
        />
      )}
    </>
  );
};
